import type { CreateConversationMessagePayload } from "#features/conversations/api/useCreateConversationMessageMutation.ts";
import { useConversationMessageCacheHandler } from "#features/conversations/hooks/useConversationMessageCacheHandler.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { AiMutationKeys } from "#lib/AiQueryKeys.ts";
import { Button } from "@hopper-ui/components";
import { IconStopFill } from "@intentui/icons";
import { useMutationState } from "@tanstack/react-query";
import { useCallback } from "react";
import "./ConversationStopButton.css";

interface Props {
    conversationId: string;
}

export const ConversationStopButton = ({ conversationId }: Props) => {
    const { tShared } = useSharedTranslation();
    const { updateMessage } = useConversationMessageCacheHandler();

    const mutationPayload = useMutationState({
        filters: {
            mutationKey: AiMutationKeys.CreateConversationMessage(),
            predicate: mutation => {
                const payload = mutation.state.variables as CreateConversationMessagePayload | undefined;

                return payload?.conversationId === conversationId;
            }
        },
        select: mutation => mutation.state.variables as CreateConversationMessagePayload | undefined
    })?.at(-1);

    const handleStop = useCallback(() => {
        if (!mutationPayload) {
            return;
        }

        const { abortController, messageId } = mutationPayload;

        abortController.abort();
        updateMessage(conversationId, messageId, message => ({
            ...message,
            assistantStatus: "cancelled"
        }));
    }, [conversationId, mutationPayload, updateMessage]);

    return (
        <Button type="button" variant="primary" size="sm" borderRadius="circle" aria-label={tShared("stop")} onPress={handleStop}>
            <IconStopFill className="wai-conversation-stop-glyph" />
        </Button>
    );
};
